// Column > Column
import React from 'react'
import styled from 'styled-components'
import classNames from 'classnames'
import BlockGroup from '../BlockGroup'
import Scale from '../ScaleData'
import BlockData from '../BlockData'
import { hasIntersection } from '../utils/Array'
import { closestToNumber } from '../utils/Number'
import { deepCopy } from '../utils/Object'
import styles from './Column.module.css'
import '../global.css'
import { ICollision, CollisionProps, ColumnProps, ColumnState } from './IColumn'

const SelectedBlockGroup = styled(BlockGroup)`
	position: absolute;
	z-index: 2;
`

const CoreUxBlockGroup = styled(BlockGroup)`
	border-left: 1px solid ${({ theme }) => theme.palette.accent.neutral};
	> :nth-child(2n) {
		border-bottom: 1px solid ${({ theme }) => theme.palette.accent.neutral};
	}
`

const HeaderWrapper = styled.div`
	border-left: 1px solid ${({ theme }) => theme.palette.accent.neutral};
	border-bottom: 1px solid ${({ theme }) => theme.palette.accent.neutral};
`

export default class Column<BlockTypesType extends object> extends React.Component<
	ColumnProps<BlockTypesType>,
	ColumnState<BlockTypesType>
> {
	// track the different dynamic values for each BlockGroup; collision details, width
	blockPropsMap: WeakMap<BlockData<BlockTypesType>, CollisionProps<BlockTypesType>> = new WeakMap()

	state = {
		isMouseDown: false,
		blocksCopy: (deepCopy(this.props.blocks) as BlockData<BlockTypesType>[]) || []
	}

	static getDerivedStateFromProps<BlockTypesType extends object>(
		props: ColumnProps<BlockTypesType>,
		state: ColumnState<BlockTypesType>
	) {
		if (!BlockData.arraysAreEqual(props.blocks, state.blocksCopy)) {
			return {
				blocksCopy: (deepCopy(props.blocks) as BlockData<BlockTypesType>[]) || []
			}
		}

		return null
	}

	private generateSelectedBlocks = () => {
		const { blocksCopy } = this.state
		const {
			isActive,
			scale,
			gridMouseDownIx,
			gridMouseEnterIx,
			gridMouseDown,
			setActiveMethod,
			showDetails,
			labelConversion
		} = this.props
		const finalBlocks = []

		// determine the collision props for the map
		this._setAllBlockCollisionProps(blocksCopy)

		// once collision props are determined the widths can be calulcated
		this._calcBlockWidths(blocksCopy)

		// finally sort the block copy object by the widths just calculated
		blocksCopy.sort((a, b) => {
			const aMapProps = this.blockPropsMap.get(a)
			const bMapProps = this.blockPropsMap.get(b)
			const aWidth = (aMapProps && aMapProps.width) || 100
			const bWidth = (bMapProps && bMapProps.width) || 100

			return bWidth - aWidth
		})

		// iterate over the now sorted Array and generate the instances for display
		for (let i = 0, n = blocksCopy.length; i < n; i++) {
			const blockData = blocksCopy[i] as BlockData<BlockTypesType>
			const indexes = blockData.indexes

			if (indexes.length > 0 && blockData.types.length > 0) {
				const groupScale = new Scale(indexes[0], indexes[indexes.length - 1] + 1, scale.increment)

				const blockMapProps = this.blockPropsMap.get(blockData)

				const newBlock = (
					<SelectedBlockGroup
						key={i}
						disabled={!isActive}
						gridMouseDownIx={gridMouseDownIx}
						gridMouseEnterIx={gridMouseEnterIx}
						data={blockData}
						// numCollisions={numCollisions}
						// closestCollision={closestCollision}
						width={blockMapProps && blockMapProps.width}
						scale={scale}
						groupScale={groupScale}
						activeMethod="modify"
						setActiveMethod={setActiveMethod}
						callActiveMethod={this._callActiveMethod}
						showDetails={showDetails}
						gridMouseDown={gridMouseDown}
						labelConversion={labelConversion}
					/>
				)

				finalBlocks.push(newBlock)
			}
		}

		return finalBlocks
	}

	// side effect to call all the necessary methods for collision props of each Block
	// sets the values to the map at the end
	private _setAllBlockCollisionProps = (blocks?: BlockData<BlockTypesType>[]) => {
		if (blocks) {
			for (let i = 0, n = blocks.length; i < n; i++) {
				const current = blocks[i]
				const collidingBlocks = this._collidingBlocks(current, blocks)
				const minCollisionIx = this._minCollisionIx(collidingBlocks)
				const closestCollision = this._closestCollision(i, collidingBlocks)

				this.blockPropsMap.set(current, {
					collidingBlocks,
					minCollisionIx,
					collisions: closestCollision && this._calcNumCollisions(i, closestCollision, minCollisionIx),
					closestCollision
				})
			}
		}
	}

	// side effect to calculate the width based on the previously determined collision props
	// sets the width value into the same map as the collision props
	_calcBlockWidths = (blocks?: BlockData<BlockTypesType>[]) => {
		if (blocks) {
			for (let i = 0, n = blocks.length; i < n; i++) {
				const BlockData = blocks[i]
				const blockMapProps = this.blockPropsMap.get(BlockData) || {}

				// how many blocks does this one over lap with and how close to the label does it get?
				const numCollisions = blockMapProps.collisions || 0
				const collisionMapProps: CollisionProps<BlockTypesType> =
					blockMapProps && blockMapProps.closestCollision
						? this.blockPropsMap.get(blockMapProps.closestCollision.block) || {}
						: {}
				const collisionWidth = collisionMapProps.width || 100
				const startingCollisionIx =
					blockMapProps && blockMapProps.closestCollision && blockMapProps.closestCollision.block.indexes[0]

				const width =
					numCollisions === 0
						? 100
						: startingCollisionIx !== undefined && BlockData.indexes[0] - startingCollisionIx < 2
							? collisionWidth * 0.5
							: collisionWidth * 0.85

				const blockPropsMap = this.blockPropsMap.get(BlockData)
				this.blockPropsMap.set(BlockData, Object.assign({}, blockPropsMap, { width }))
			}
		}
	}

	/**
	 * calculates how close to the previous block label a block is
	 */
	_closestCollisionIx = (block: BlockData<BlockTypesType>, collisions: ICollision<BlockTypesType>[]): number => {
		const firstIx = block.indexes[0]
		let closestIx = closestToNumber(collisions[0].block.indexes, firstIx)

		for (let i = 1, n = collisions.length; i < n; i++) {
			const current = collisions[i]
			const currentClosest = closestToNumber(current.block.indexes, firstIx)

			if (currentClosest < closestIx && currentClosest > -1) {
				closestIx = currentClosest
			}
		}

		return closestIx
	}

	/**
	 * returns the lowest value index of the collision set
	 */
	_minCollisionIx = (collisions: ICollision<BlockTypesType>[]): number => {
		const collisionIxs = collisions.map(c => c.index)
		const minIx = collisionIxs.length > 0 ? Math.min(...collisionIxs) : -1

		return minIx
	}

	/**
	 * finds the closest colliding block
	 */
	_closestCollision = (
		blockIx: number,
		collisions: ICollision<BlockTypesType>[]
	): ICollision<BlockTypesType> | undefined => {
		const collisionIxs = collisions.map(c => c.index)

		const closestCollisionIx = collisionIxs.indexOf(blockIx - 1)

		return closestCollisionIx !== undefined && closestCollisionIx > -1 ? collisions[closestCollisionIx] : undefined
	}

	/**
	 * calculates the number of collisions for a block
	 */
	_calcNumCollisions = (
		blockIx: number,
		closestCollision: ICollision<BlockTypesType>,
		minCollisionIx: number
	): number => {
		let result = 0

		if (blockIx === minCollisionIx) return 0
		else if (closestCollision.index === minCollisionIx) return 1
		else {
			const blockMapProps = closestCollision ? this.blockPropsMap.get(closestCollision.block) : undefined

			if (blockMapProps) {
				result = blockMapProps.collisions ? blockMapProps.collisions + 1 : 0
			}
		}

		return result
	}

	/**
	 * calculates the number of blocks the specified block overlaps with
	 */
	_collidingBlocks = (
		block: BlockData<BlockTypesType>,
		blocks: BlockData<BlockTypesType>[]
	): ICollision<BlockTypesType>[] => {
		const response = []
		const currentIxs = block.indexes
		let blockIx = undefined

		for (let i = 0, n = blocks.length; i < n; i++) {
			if (blocks[i] === block) {
				blockIx = i
				continue
			}

			const compareIxs = blocks[i].indexes

			if (hasIntersection(blocks[i].indexes, block.indexes) && currentIxs[0] >= compareIxs[0]) {
				if (currentIxs[0] === compareIxs[0] && blockIx !== undefined) {
					continue
				}

				response.push({ index: i, block: blocks[i] })
			}
		}

		return response
	}

	_setMouseDown = (isMouseDown: boolean) => {
		this.setState({ isMouseDown })
	}

	/**
	 * partial application of the 'callActiveMethod' prop
	 */
	_callActiveMethod = (indexes: number[], blockToModify?: BlockData<BlockTypesType>) => {
		const { callActiveMethod, ix: colIx } = this.props

		callActiveMethod(indexes, colIx, blockToModify)
	}

	/**
	 * calls the callback provided in props with the index value of the column
	 */
	private toggleSelect = () => {
		const { ix, toggleActive } = this.props

		toggleActive(ix)
	}

	render() {
		const {
			gridMouseDownIx,
			gridMouseEnterIx,
			title,
			autoActivate,
			isActive,
			scale,
			blocks,
			gridMouseDown,
			setActiveMethod,
			style
		} = this.props

		return (
			<div
				className={classNames(styles['column-root'], {
					[styles['column-active']]: !autoActivate && isActive
				})}
				style={style}
			>
				<HeaderWrapper
					onClick={this.toggleSelect}
					className={classNames(styles['column-title-wrapper'], {
						[styles['auto-activate']]: autoActivate
					})}
				>
					<h1 className={classNames(styles['column-title'], styles['disable-select'])}>{title}</h1>
				</HeaderWrapper>
				<div className={styles['column-blocks']}>
					{blocks && this.generateSelectedBlocks()}
					<CoreUxBlockGroup
						disabled={!isActive}
						gridMouseDownIx={gridMouseDownIx}
						gridMouseEnterIx={gridMouseEnterIx}
						scale={scale}
						activeMethod="add"
						setActiveMethod={setActiveMethod}
						callActiveMethod={this._callActiveMethod}
						gridMouseDown={gridMouseDown}
					/>
				</div>
			</div>
		)
	}
}
